import {
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogActions,
  Grid,
  Stack,
  TextField,
  Typography,
  Accordion,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { MenuItem } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { FormProvider, RHFTextField, RHFSelect } from 'src/components/hook-form';
import { AccordionDetails } from '@mui/material';
import { AccordionSummary } from '@mui/material';
import { useGroups, usePatients } from 'src/careflow/ph-core/fhir-client/fhir-react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { GroupWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Group';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import CancelButton from 'src/careflow/common/CancelButton';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import DialogCloseButton, { useUnsavedCloseGuard } from 'src/components/DialogCloseButton';

type FormValue = {
  lastName: string;
  firstName: string;
  dateOfBirth: Date;
  identifier: string;
  middleName: string;
  race: string;
  addressone: string;
  addresstwo: string;
  zipCode: string;
  city: string;
  state: string;
};

type Props = {
  patient: WrappedPatient;
  open: boolean;
  onClose: VoidFunction;
  id: string;
  PATH_PERMISIONS: string;
};

export default function AddNewMemberGroup({ id, patient, open, PATH_PERMISIONS, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [family, setFamily] = useState('')
  const [given, setGiven] = useState('')
  const [autofetch, setAutoFetch] = useState(false)
  const [booleanSearch, setBooleanSearch] = useState(false)
  const [viewMoreExpanded, setViewMoreExpanded] = useState(false)
  const { configurations } = useTenantConfigData();
  const {
    valueSets: [raceCategories, identifiers],
  } = useValueSetsByIdentifiers([
    'omb-race-category',
    'patient-identifier-types',
  ]);

  const patientSearchFilter = useMemo(() => {
    const filter: Record<string, string> = {};
    if (given?.trim()) filter['given:contains'] = given.trim();
    if (family?.trim()) filter['family:contains'] = family.trim();
    return filter;
  }, [given, family]);

  const [patientsListSearch, { isLoading }] = usePatients({
    filter: patientSearchFilter,
    autofetch: autofetch,
  });
  const { i18n } = useLocales();

  const [, { patch: updateGroup }] = useGroups({ map: GroupWrapper });
  const defaultValues = useMemo(
    () =>
      ({
        lastName: '',
        firstName: '',
        dateOfBirth: new Date(),
        identifier: '',
        middleName: '',
        race: '',
        addressone: '',
        addresstwo: '',
        zipCode: '',
        state: '',
        city: '',
      } as FormValue),
    [patient]
  );

  const EventSchema = Yup.object().shape({
    lastName: Yup.string()
      .required('Last Name is required')
      .matches(/^[a-zA-Z\s-]+$/, 'Last Name cannot contain numbers'),
    firstName: Yup.string()
      .required('First Name is required')
      .matches(/^[a-zA-Z\s]+$/, 'First Name cannot contain numbers'),
    dateOfBirth: Yup.date().optional(),
  });

  const methods = useForm({ resolver: yupResolver(EventSchema), defaultValues });

  const { reset, handleSubmit } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [open, defaultValues]);

  const handleClose = () => {
    onClose();
  };

  const requestClose = useUnsavedCloseGuard(handleClose, methods.formState.isDirty);

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      width: 140,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.name?.[0]?.given?.[0] ?? '',
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      width: 140,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.name?.[0]?.family ?? '',
    },
    {
      field: 'birthDate',
      headerName: 'Birth Date',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      width: 120,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.birthDate ?? '',
    },
    {
      field: 'mrn',
      headerName: 'MRN',
      description: 'Medical Record Number.',
      sortable: false,
      width: 140,
      valueGetter: (params: GridValueGetterParams) =>
        PatientWrapper(params.row as Parameters<typeof PatientWrapper>[0]).getMRN(configurations?.mpi?.systemMrn)?.value ?? '',
    },
  ];
  const onSubmit = async (data: FormValue) => {
    try {
      setFamily(data.lastName.trim());
      setGiven(data.firstName.trim());
      setAutoFetch(true);
      setBooleanSearch(true);
      enqueueSnackbar(i18n('patients.details.groups.searching', 'crs') || 'Searching...');
    } catch {
      handleClose();
      enqueueSnackbar('Patient was not updated.', { variant: 'error' });
    }
  };
  const updateGroupFunction = async () => {
    try {
      await updateGroup([
        {
          id: `${id}`,
          patches: [
            {
              op: 'add',
              path: ['member/-'],
              value: {
                entity: {
                  id: '627df988-d1b1-40cd-8a98-6ffdbc1e93f6',
                  reference: `Patient/${selectedRowId}`,
                  type: 'Patient',
                },
                period: {
                  end: '2023-07-31T07:00:00.000Z',
                },
              },
            },
          ],
        },
      ]);
      handleClose();
      enqueueSnackbar(`${i18n('patients.details.groups.msgUpdate', 'crs')}`);
    } catch {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
      handleClose();
    }
  };
  if (!open) return null;
  const handleRowSelection = (params: any) => {
    if (params.id == selectedRowId) {
      setSelectedRowId(null);
    } else {
      setSelectedRowId(params.id);
    }
  };
  return (
    <Dialog
      keepMounted={false}
      open={true}
      onClose={() => void requestClose()}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogCloseButton onClose={handleClose} hasUnsavedChanges={methods.formState.isDirty} />
      {booleanSearch === false ? (
        <>
          <DialogTitle>{i18n('patients.details.groups.newMember', 'crs')}</DialogTitle>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Card sx={{ m: 2 }}>
              <Grid container>
                <Grid item xs={12}>
                  <Stack spacing={2} sx={{ p: 2 }}>
                    <Typography variant="body2">{i18n('patients.lastName', 'crs')}*</Typography>
                    <RHFTextField
                      name="lastName"
                      label={i18n('patients.details.groups.typeLastName', 'crs')}
                    />

                    <Typography variant="body2">{i18n('patients.firstName', 'crs')}*</Typography>
                    <RHFTextField
                      name="firstName"
                      label={i18n('patients.details.groups.typeFirstName', 'crs')}
                    />

                    {checkAclValidation({ acls: [crsAcls.CRS[PATH_PERMISIONS].GROUPS.MEMBERS.VIEW_MORE] }) && (
                      <Accordion
                        expanded={viewMoreExpanded}
                        onChange={(_, expanded) => setViewMoreExpanded(expanded)}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          {viewMoreExpanded
                            ? i18n('patients.details.groups.viewLess', 'crs')
                            : i18n('patients.details.groups.viewMore', 'crs')}
                        </AccordionSummary>
                        <AccordionDetails style={{ width: '100%' }}>
                          <Stack spacing={2} sx={{ p: 0 }}>
                            <Grid style={{ display: 'flex' }} item xs={12}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {i18n('patients.details.groups.identifier', 'crs')}
                                </Typography>
                                <Stack spacing={2} sx={{ p: 1 }}>
                                  <RHFSelect
                                    style={{ width: '80%' }}
                                    name="identifier"
                                    label="Select One"
                                  >
                                    <MenuItem disabled></MenuItem>
                                    {identifiers?.asList().map((option) => (
                                      <MenuItem key="" value={option.display}>
                                        {option.display}
                                      </MenuItem>
                                    ))}
                                  </RHFSelect>
                                </Stack>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {i18n('patients.details.groups.value', 'crs')}
                                </Typography>
                                <Stack spacing={2} sx={{ p: 1 }}>
                                  <RHFTextField
                                    name="identifier"
                                    label={i18n('patients.details.groups.groupCode', 'crs')}
                                  />
                                </Stack>
                              </Grid>
                            </Grid>
                            <Typography variant="body2">
                              {i18n('patients.middleName', 'crs')}
                            </Typography>
                            <RHFTextField
                              name="middleName"
                              label={i18n('patients.details.groups.typeMiddleName', 'crs')}
                            />
                            <Typography variant="body2">
                              {i18n('patients.details.groups.race', 'crs')}
                            </Typography>
                            <RHFSelect name="race" label="Select One">
                              <MenuItem disabled></MenuItem>
                              {raceCategories?.asList().map((option) => (
                                <MenuItem key={option.code} value={option.display}>
                                  {option.display}
                                </MenuItem>
                              ))}
                            </RHFSelect>
                            <Typography variant="body2">
                              {i18n('patients.details.groups.addressOne', 'crs')}
                            </Typography>
                            <RHFTextField
                              name="addressone"
                              label={i18n('patients.details.groups.typeAddressOne', 'crs')}
                            />
                            <Typography variant="body2">
                              {i18n('patients.details.groups.addressTwo', 'crs')}
                            </Typography>
                            <RHFTextField
                              name="addresstwo"
                              label={i18n('patients.details.groups.typeAddressTwo', 'crs')}
                            />
                            <Grid style={{ display: 'flex' }} item xs={12}>
                              <Grid item xs={4}>
                                <Typography variant="body2">
                                  {i18n('patients.details.groups.zipCode', 'crs')}
                                </Typography>
                                <Stack spacing={2} sx={{ p: 1 }}>
                                  <RHFTextField
                                    name="zipCode"
                                    label={i18n('patients.details.groups.typeZipCode', 'crs')}
                                  />
                                </Stack>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2">
                                  {i18n('patients.details.groups.city', 'crs')}
                                </Typography>
                                <Stack spacing={2} sx={{ p: 1 }}>
                                  <RHFTextField name="city" label="Select One" />
                                </Stack>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2">
                                  {i18n('patients.details.groups.state', 'crs')}
                                </Typography>
                                <Stack spacing={2} sx={{ p: 1 }}>
                                  <RHFTextField name="state" label="Select One" />
                                </Stack>
                              </Grid>
                            </Grid>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Stack>
                </Grid>
              </Grid>
              <Stack spacing={2} alignItems="center">
                <DialogActions>
                  <Box sx={{ flexGrow: 1 }} />
                  <CancelButton onClick={() => void requestClose()}>{i18n('cancel')}</CancelButton>

                  <Button variant="contained" type="submit">
                    {i18n('patients.details.groups.search', 'crs')}
                  </Button>
                </DialogActions>
              </Stack>
            </Card>
          </FormProvider>
        </>
      ) : (
        <>
          <DialogTitle>{i18n('patients.details.groups.resultSearch', 'crs')}</DialogTitle>
          <Card style={{ margin: '30px' }}>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={patientsListSearch}
                loading={isLoading}
                columns={columns}
                onCellClick={handleRowSelection}
                selectionModel={selectedRowId ? [selectedRowId] : []}
                checkboxSelection
                initialState={{
                  pagination: {
                    pageSize: 5,
                  },
                }}
                sx={{
                  '& .MuiDataGrid-row': {
                    borderBottom: '1px solid #EAECF0',
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#FAFAFA',
                    },
                    '&:hover': {
                      backgroundColor: '#F5F6F7',
                    },
                  },
                }}
              />
            </Box>
            <Stack spacing={2} alignItems="center">
              <DialogActions>
                <Box sx={{ flexGrow: 1 }} />
                <CancelButton
                  onClick={() => {
                    setBooleanSearch(false);
                  }}
                >
                  {i18n('cancel')}
                </CancelButton>

                <Button
                  disabled={selectedRowId == null}
                  variant="contained"
                  onClick={updateGroupFunction}
                >
                  {i18n('patients.details.groups.accept', 'crs')}
                </Button>
              </DialogActions>
            </Stack>
          </Card>
        </>
      )}
    </Dialog>
  );
}
